import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MonoLabel } from "./shared";
import { LQIP } from "./lqip";

const FRAMES = [
  {
    src: "/photos/alignment-composite-web.jpg",
    w: 1536,
    h: 1024,
    cap: "Frames 01–02 — Alignment service",
    span: "lg:col-span-7",
  },
  {
    src: "/photos/shop-interior-web.jpg",
    w: 1400,
    h: 933,
    cap: "Frame 03 — The floor at 580 Jersey Ave",
    span: "lg:col-span-5",
  },
];

function Frame({ f, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-20, 20]);

  return (
    <Reveal delay={0.1 + i * 0.08} y={40} className={f.span}>
      <div ref={ref} className="group relative">
        <div
          className="absolute -inset-0 translate-x-3 translate-y-3 border border-gold/50 transition-transform duration-500 group-hover:translate-x-4 group-hover:translate-y-4"
          aria-hidden="true"
        />
        {/* Fixed 3:2 box so the frame never depends on the image for its
            height; a blurred inline preview paints instantly underneath. */}
        <div
          className="relative aspect-[3/2] w-full overflow-hidden border border-line bg-panel bg-cover bg-center"
          style={{ backgroundImage: LQIP[f.src] ? `url(${LQIP[f.src]})` : undefined }}
        >
          <motion.img
            src={f.src}
            alt={f.cap}
            width={f.w}
            height={f.h}
            style={{ y }}
            loading="eager"
            decoding="async"
            data-testid={`shop-floor-image-${i + 1}`}
            className="absolute inset-x-0 -top-6 block h-[calc(100%+3rem)] w-full max-w-none object-cover"
          />
          <span className="absolute -left-px -top-px h-6 w-6 border-l-2 border-t-2 border-gold" />
          <span className="absolute -bottom-px -right-px h-6 w-6 border-b-2 border-r-2 border-gold" />
        </div>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-zinc-300">
          {f.cap}
        </p>
      </div>
    </Reveal>
  );
}

export default function ShopShowcase() {
  return (
    <section
      data-testid="shop-floor-showcase"
      className="relative overflow-hidden border-t border-line/70 bg-panel/40 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8 lg:px-14">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <MonoLabel>The Shop Floor</MonoLabel>
            <h2 className="mt-4 font-display text-5xl uppercase leading-[0.9] tracking-wide sm:text-6xl lg:text-7xl">
              Real Work, <span className="text-gold">Real Equipment</span>
            </h2>
          </div>
          <p className="max-w-xs pb-2 text-sm leading-relaxed text-zinc-300">
            Straight from the bays at 580 Jersey Ave — the John Bean
            alignment rig and honest, everyday service work.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
          {FRAMES.map((f, i) => (
            <Frame key={f.cap} f={f} i={i} />
          ))}
        </div>

        <p className="mt-8 text-right font-mono text-xs uppercase tracking-[0.25em] text-gold">
          John Bean equipped
        </p>
      </div>
    </section>
  );
}
